import { Link } from "@tanstack/react-router";
import { Reveal } from "./Reveal";
import { CALENDLY_URL } from "@/lib/site";
import { trackScheduleConsultation } from "@/lib/analytics";

export function CtaPanel({
  title = "Ready to simplify your finances?",
  body = "Talk to a Finendra specialist about your books, your systems, and what a dedicated finance pod would handle for you.",
  ctaLabel = "Schedule a Consultation",
  ctaHref,
  service,
  industry,
}: {
  title?: string;
  body?: string;
  ctaLabel?: string;
  ctaHref?: string;
  /** Pre-fills the contact form's service select. */
  service?: string;
  /** Pre-fills the contact form's industry select. */
  industry?: string;
}) {
  const targetHref =
    ctaHref ?? (ctaLabel === "Schedule a Consultation" ? CALENDLY_URL : "/contact");
  const isExternal = /^https?:\/\//i.test(targetHref);

  return (
    <section className="px-6 py-28">
      <Reveal className="mx-auto max-w-4xl rounded-[40px] border border-brand-navy/5 bg-brand-surface p-10 text-center md:p-16">
        <h2 className="font-display text-4xl font-bold text-brand-navy md:text-5xl">{title}</h2>
        <p className="mx-auto mt-6 max-w-2xl text-lg text-brand-navy/60">{body}</p>
        <div className="mt-10">
          {isExternal ? (
            <a
              href={targetHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                if (
                  targetHref.includes("calendly.com") ||
                  ctaLabel.toLowerCase().includes("consultation")
                ) {
                  trackScheduleConsultation("cta_panel", { targetHref, title });
                }
              }}
              data-analytics-location="cta_panel"
              className="inline-block rounded-2xl bg-brand-accent px-10 py-5 text-lg font-bold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:shadow-2xl hover:shadow-brand-accent/30 hover:-translate-y-0.5 active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
            >
              {ctaLabel}
            </a>
          ) : (
            <Link
              to="/contact"
              search={{ service, industry }}
              className="inline-block rounded-2xl bg-brand-accent px-10 py-5 text-lg font-bold text-background transition-[background-color,box-shadow,transform] duration-150 ease-out hover:shadow-2xl hover:shadow-brand-accent/30 hover:-translate-y-0.5 active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
            >
              {ctaLabel}
            </Link>
          )}
        </div>
      </Reveal>
    </section>
  );
}

export function PageHero({
  eyebrow,
  title,
  body,
}: {
  eyebrow: string;
  title: string;
  body: string;
}) {
  return (
    <section className="px-6 pb-16 pt-24 md:pt-32">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">{eyebrow}</p>
          <h1 className="mt-5 max-w-3xl font-display text-5xl font-extrabold leading-[1.1] text-brand-navy md:text-6xl">
            {title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-brand-navy/60">{body}</p>
        </Reveal>
      </div>
    </section>
  );
}
